import * as React from 'react'
import { JsonLd } from 'react-schemaorg'
import { Offer as SchemaOffer, Product as SchemaProduct, UnitPriceSpecification } from 'schema-dts'

import {
  ProductCatalogProduct,
  ProductCatalogVariant,
  ProductCatalogVariantGroup,
} from '../../../../__generated__/graphql/catalog/graphql'
import { createCanonicalPdpUrl } from '../../../../app/_components/navigation/createPdpUrl'
import { canonicalBaseUrls } from '../../../../constants/figs'
import { useProductReviews } from '../../../../lib/hooks/useProductReviews'
import { ProductReview } from '../../../../types/graphql'

const figsLogo =
  'https://cdn.shopify.com/s/files/1/0139/8942/files/FIGS_LOGO_BLACK-02-large.png?1998072' as const

type PdpSchemaTagsProduct = Pick<ProductCatalogProduct, 'handle'>

type PdpSchemaTagsVariantGroup = {
  images: Array<{ url: string }> | null | undefined
  color:
    | {
        id: string
        displayValue: string
      }
    | null
    | undefined
  exclusive: boolean
  gender: ProductCatalogVariantGroup['gender']
  title: string
  description: string | null | undefined
  materials: ProductCatalogVariantGroup['materials']
  urlPath: string
  variants: Array<{
    fit?: null | {
      id: string
    }
    size?: null | {
      id: string
    }
    price: {
      amount: number
      currencyCode: string
    }
    compareAtPrice?: {
      amount: number
    } | null
    availability: ProductCatalogVariant['availability']
    urlPath: string
    upc: string
    sku: string
  }>
}

type PdpSchemaTagsVariant = Partial<
  Pick<NonNullable<ProductCatalogVariant>, 'upc' | 'sku' | 'urlPath' | 'shopifyProductId'>
>

function generatePriceSpecification(
  variant: PdpSchemaTagsVariantGroup['variants'][number]
): UnitPriceSpecification[] {
  const regularPriceInDollars = variant.compareAtPrice?.amount ?? variant.price.amount
  const discountPriceInDollars = variant.compareAtPrice?.amount ? variant.price.amount : null
  const currency = variant.price.currencyCode

  const specifications: UnitPriceSpecification[] = [
    {
      '@type': 'UnitPriceSpecification',
      'price': regularPriceInDollars,
      'priceCurrency': currency,
      'priceType': discountPriceInDollars ? 'https://schema.org/ListPrice' : undefined,
    },
  ]

  if (discountPriceInDollars) {
    specifications.push({
      '@type': 'UnitPriceSpecification',
      'price': discountPriceInDollars,
      'priceCurrency': currency,
    })
  }

  return specifications
}

function findvariantGroupImageUrls(variantGroup: PdpSchemaTagsVariantGroup): Array<string> {
  const urls = variantGroup.images?.map(img => img.url) ?? []
  return urls.length > 0 ? urls : [figsLogo]
}

type generateOfferProps = {
  variant: PdpSchemaTagsVariantGroup['variants'][number]
  variantGroup: PdpSchemaTagsVariantGroup
}

function generateOffer({ variant, variantGroup }: generateOfferProps): SchemaOffer {
  const imageUrl = findvariantGroupImageUrls(variantGroup)[0]
  const today = new Date(Date.now()).toISOString().substring(0, 10)
  const url = `https://www.wearfigs.com${variant.urlPath}`

  const validSchema: SchemaOffer = {
    '@type': 'Offer',
    'image': imageUrl,
    'priceValidUntil': today,
    'url': url,
    'sku': variant.sku,
    'gtin': variant.upc,
    'priceSpecification': generatePriceSpecification(variant),
  }

  // TODO: Figure out why we have 'availability' here even though
  // after a schema version upgrade it says that we shouldn't.
  // We need to do forced casting here to jam it into the object.
  return {
    ...validSchema,
    availability:
      variant.availability === 'AVAILABLE'
        ? 'http://schema.org/InStock'
        : 'http://schema.org/SoldOut',
  } as unknown as SchemaOffer
}

type PdpSchemaTagsProps = {
  product: PdpSchemaTagsProduct
  variantGroup: PdpSchemaTagsVariantGroup
  variant: PdpSchemaTagsVariant
}

export const ProductPageSchemaTags: React.FC<PdpSchemaTagsProps> = ({
  product,
  variantGroup,
  variant,
}) => {
  const { productReviews } = useProductReviews({
    shopifyProductId: variant.shopifyProductId,
  })

  const canonicalUrl = createCanonicalPdpUrl({
    productGroupHandle: product.handle,
    isKit: false,
    isExclusive: variantGroup.exclusive,
    genderKey: variantGroup.gender,
  })
  const url = `https://www.wearfigs.com${variant.urlPath}`
  const sku = variant.sku
  const upc = variant.upc

  const offers = variantGroup.variants?.map(nextVariant =>
    generateOffer({ variant: nextVariant, variantGroup })
  )

  const materials = variantGroup.materials?.join('; ')

  return (
    <JsonLd<SchemaProduct>
      item={{
        '@context': 'https://schema.org',
        '@id': canonicalUrl,
        '@type': 'Product',
        'aggregateRating':
          productReviews?.averageScore && productReviews?.totalReviews
            ? {
                '@type': 'AggregateRating',
                'reviewCount': productReviews.totalReviews,
                'ratingValue': productReviews.averageScore,
              }
            : undefined,
        'brand': {
          '@type': 'Brand',
          'name': 'FIGS',
          'logo': {
            '@type': 'ImageObject',
            'name': 'FIGS Logo',
            'contentUrl': figsLogo,
          },
          'slogan': 'Why wear scrubs, when you can wear FIGS?',
          'url': canonicalBaseUrls.home,
        },
        'name': variantGroup.title,
        'description': variantGroup.description ?? undefined,
        'color': variantGroup.color?.displayValue,
        'image': findvariantGroupImageUrls(variantGroup),
        'manufacturer': {
          '@type': 'Organization',
          'name': 'FIGS INC.',
          'url': 'https://www.wearfigs.com',
        },
        'material': materials,
        'offers': offers,
        'sku': sku,
        'url': url,
        'gtin': upc,
        'gtin12': upc,
        'review':
          productReviews?.reviews.map(({ reviewer, title, score, content }: ProductReview) => ({
            '@type': 'Review',
            'author': {
              '@type': 'Person',
              'name': reviewer,
            },
            'name': title,
            'description': variantGroup.description ?? undefined,
            'itemReviewed': variantGroup.title,
            'reviewBody': content,
            'reviewRating': {
              '@type': 'Rating',
              'ratingValue': score,
            },
          })) || [],
      }}
    />
  )
}
